import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import api from "../../../utils/api";
import NextPrevPagination from "../../../components/NextPrevPagination";
import ToyotaFleetRegistrationComponent from "./ToyotaFleetRegistration";
import StellantisFleetRegistration from "./StellantisFleetRegistration";
import {
  StellantisContract,
  StellantisEligibility,
  ToyotaFleetRegistration,
} from "../types";

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (!error) return fallback;
  const anyErr = error as any;
  const isHtml = (s: string) => /^\s*<(!doctype|html)/i.test(s);
  const message = anyErr?.message || anyErr?.detail;
  if (
    typeof message === "string" &&
    message.trim().length > 0 &&
    !isHtml(message)
  )
    return message;
  if (typeof anyErr === "string") return isHtml(anyErr) ? fallback : anyErr;
  try {
    return JSON.stringify(anyErr);
  } catch {
    return fallback;
  }
};

interface CloudLogEntry {
  timestamp: string;
  payload?: string | object | null;
}

/**
 * A log entry can reach us without a payload. JSON.stringify would turn that
 * into the literal text "null", so render a placeholder instead.
 */
const formatLogPayload = (payload: CloudLogEntry["payload"]): string => {
  if (payload === null || payload === undefined || payload === "") {
    return "—";
  }
  return typeof payload === "string"
    ? payload
    : JSON.stringify(payload, null, 2);
};

interface CloudLogsResponse {
  plate: string;
  vehicleId: number;
  search_params: {
    hours: number;
    limit: number;
    startTime: string;
    endTime: string;
  };
  results: CloudLogEntry[];
  totalEntries: number;
}

interface ToyotaFleetStatus {
  id: number;
  created: string;
  edited: string;
  lastChecked: string;
  vehicle: number;
  vehiclePlate: string;
  vehicleVin: string;
  vehicleMake: string;
  vehicleModel: string;
  active: boolean;
  trackingSupported: boolean;
  vinNumber: string;
}

interface LocationStatusProps {
  vehicleId: number;
  vehiclePlate?: string;
  stellantisEligibility?: StellantisEligibility | null;
  stellantisContract?: StellantisContract | null;
  isSuperuser?: boolean;
  onUpdate?: () => void;
}

/**
 * LocationStatus component displays Toyota fleet status and cloud logs for a vehicle.
 *
 * Cloud logs are fetched using VIN (from fleet status) when available,
 * falling back to vehicle plate if VIN is not available.
 */
const LocationStatus: React.FC<LocationStatusProps> = ({
  vehicleId,
  vehiclePlate,
  stellantisEligibility,
  stellantisContract,
  isSuperuser = false,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CloudLogsResponse | null>(null);
  const [fleetStatus, setFleetStatus] = useState<ToyotaFleetStatus | null>(
    null,
  );
  const [fleetStatusLoading, setFleetStatusLoading] = useState(false);
  const [fleetStatusError, setFleetStatusError] = useState<string | null>(null);
  const [registration, setRegistration] =
    useState<ToyotaFleetRegistration | null>(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(
    null,
  );
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const fetchFleetStatus = async () => {
    if (!vehiclePlate) return;

    setFleetStatusLoading(true);
    setFleetStatusError(null);

    try {
      const response = await api.get(
        `/vehicles/toyota-fleet-status/${vehiclePlate}/`,
      );

      if (response.error) {
        setFleetStatusError(
          extractErrorMessage(response.error, "Failed to fetch fleet status"),
        );
      } else {
        setFleetStatus(response.data);
      }
    } catch (err) {
      setFleetStatusError(
        extractErrorMessage(
          err,
          "Failed to fetch fleet status. Please try again.",
        ),
      );
    } finally {
      setFleetStatusLoading(false);
    }
  };

  const fetchRegistration = async () => {
    setRegistrationLoading(true);
    setRegistrationError(null);

    try {
      const response = await api.get(`/vehicles/${vehicleId}/`);

      if (response.error) {
        setRegistrationError(
          extractErrorMessage(
            response.error,
            "Failed to fetch registration status",
          ),
        );
      } else {
        setRegistration(response.data.toyotaFleetRegistration || null);
      }
    } catch (err) {
      setRegistrationError(
        extractErrorMessage(
          err,
          "Failed to fetch registration status. Please try again.",
        ),
      );
    } finally {
      setRegistrationLoading(false);
    }
  };

  const fetchData = async (pageNumber: number = 1, hours: number = 24) => {
    // Raw cloud logs are superuser-only; skip the request otherwise.
    if (!isSuperuser) return;
    // Use VIN from fleet status if available, otherwise fall back to plate
    const identifier = fleetStatus?.vinNumber || vehiclePlate;
    if (!identifier) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/cloud-logs/${identifier}/`, {
        params: {
          hours,
          limit: pageSize,
          offset: (pageNumber - 1) * pageSize,
        },
      });

      if (response.error) {
        setError(
          extractErrorMessage(response.error, "Failed to fetch cloud logs"),
        );
      } else {
        setData(response.data);
      }
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          "Failed to fetch cloud logs. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFleetStatus();
  }, [vehiclePlate]);

  useEffect(() => {
    fetchRegistration();
  }, [vehicleId]);

  useEffect(() => {
    fetchData(page);
  }, [vehicleId, vehiclePlate, page, fleetStatus]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm:ss");
    } catch {
      return dateString;
    }
  };

  const getStatusChip = (active: boolean, trackingSupported: boolean) => {
    // Check if manually deactivated first
    if (registration?.isRegistered) {
      const getDeactivationText = (daysRemaining: number | null) => {
        if (daysRemaining === null) return "Manually Deactivated (Forever)";
        if (daysRemaining <= 0) return "Manual Deactivation Expired";
        if (daysRemaining === 1)
          return "Manually Deactivated (1 day remaining)";
        return `Manually Deactivated (${daysRemaining} days remaining)`;
      };

      return (
        <Chip
          label={getDeactivationText(registration.daysRemaining)}
          color="warning"
          size="small"
          sx={{ backgroundColor: "#ff9800", color: "white" }}
        />
      );
    }

    // Original status logic
    if (!active) {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    if (trackingSupported) {
      return <Chip label="Tracking Active" color="success" size="small" />;
    }
    return <Chip label="Active" color="warning" size="small" />;
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Current Fleet Status Section */}
      {fleetStatusLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : fleetStatusError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {fleetStatusError}
        </Alert>
      ) : fleetStatus ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Current Fleet Status
          </Typography>

          {/* Main Status Display */}
          <Box
            sx={{
              mb: 3,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {getStatusChip(fleetStatus.active, fleetStatus.trackingSupported)}
            <Chip
              label={
                fleetStatus.trackingSupported
                  ? "Tracking Supported"
                  : "Tracking Not Supported"
              }
              color={fleetStatus.trackingSupported ? "success" : "default"}
              size="small"
              variant="outlined"
            />
          </Box>

          {/* Vehicle Information */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Vehicle
              </Typography>
              <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
                {fleetStatus.vehicleMake} {fleetStatus.vehicleModel}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Last Checked
              </Typography>
              <Typography variant="body1">
                {formatDate(fleetStatus.lastChecked)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Created
              </Typography>
              <Typography variant="body1">
                {formatDate(fleetStatus.created)}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1, fontWeight: 500 }}
              >
                Last Modified
              </Typography>
              <Typography variant="body1">
                {formatDate(fleetStatus.edited)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      ) : null}

      {/* Toyota Fleet Registration Component */}
      <ToyotaFleetRegistrationComponent
        vehicleId={vehicleId}
        registration={registration}
        onUpdate={() => {
          fetchRegistration();
          onUpdate?.();
        }}
      />

      <StellantisFleetRegistration
        vehicleId={vehicleId}
        eligibility={stellantisEligibility}
        contract={stellantisContract}
        onUpdate={() => onUpdate?.()}
      />

      {isSuperuser && (
        <>
          <Typography variant="h6" gutterBottom>
            Logs
            {fleetStatus?.vinNumber && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: "normal" }}
              >
                (VIN: {fleetStatus.vinNumber})
              </Typography>
            )}
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : !data || data.results.length === 0 ? (
            <Box
              sx={{ mt: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}
            >
              <Typography variant="body1" color="text.secondary">
                No cloud logs found for this vehicle
                {fleetStatus?.vinNumber
                  ? ` (VIN: ${fleetStatus.vinNumber})`
                  : vehiclePlate
                    ? ` (Plate: ${vehiclePlate})`
                    : ""}
                .
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "#f0f0f0",
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {data.results.length} of {data.totalEntries} entries
                  (Page {page} of {Math.ceil(data.totalEntries / pageSize)})
                  {fleetStatus?.vinNumber && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      • VIN: {fleetStatus.vinNumber}
                    </Typography>
                  )}
                </Typography>
              </Box>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Payload</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.results.map((log, index) => (
                      <TableRow key={`${log.timestamp}-${index}`} hover>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(log.timestamp)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                          >
                            {formatLogPayload(log.payload)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {data.totalEntries >= pageSize && (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <NextPrevPagination
                    limit={pageSize}
                    total={data.totalEntries + 1}
                    page={page}
                    onPageChange={handlePageChange}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default LocationStatus;
